import { FC, useEffect, useState, useMemo } from 'react'
import { useResourceApproversPaginated } from 'hooks'
import { usePageContext, PageContextProvider } from 'pageContext'
import { useDebounce } from 'packages/core/hooks'
import { PeopleListCard } from 'components'

export type ApproversProps = {
    resourceId: string
    label?: string
    initialPageSize?: number
    expandedPageSize?: number
    contextKey?: string
}

// The inner component that uses the context
const ResourceApproversListInner: FC<Omit<ApproversProps, 'contextKey'>> = ({
    resourceId,
    label = 'Approvers',
    initialPageSize = 2,
    expandedPageSize = 8,
}) => {
    // Context and pagination state
    const [{ searchTerm }, dispatch] = usePageContext() as any
    const [expanded, setExpanded] = useState(false)
    const [searchKey, setSearchKey] = useState(searchTerm || '')

    // Debounce search to avoid excessive API calls
    const debouncedSearchValue = useDebounce(searchKey, 500)

    // Calculate page size based on expanded state
    const pageSize = expanded ? expandedPageSize : initialPageSize

    // Memoize pagination props to prevent unnecessary re-renders
    const paginationProps = useMemo(
        () => ({
            size: 'small',
            showPageSelection: false,
            showItemsPerPageSelection: false,
            variant: 'rounded',
            showCountStat: false,
        }),
        [],
    )

    // Fetch resource owners with pagination
    const {
        list,
        loading: isLoading,
        pagination,
        totalCount,
    } = useResourceApproversPaginated(resourceId, pageSize, paginationProps)

    // Update search term in context when debounced value changes
    useEffect(() => {
        dispatch({
            type: 'SET_PROP',
            payload: { key: 'searchTerm', value: debouncedSearchValue },
        })
    }, [debouncedSearchValue, dispatch])

    // Toggle between initial and expanded page size
    const togglePageSize = () => {
        dispatch({
            type: 'SET_PAGE',
            payload: 1,
        })
        setExpanded((prev) => !prev)
    }

    // Don't render anything if resourceId is missing
    if (!resourceId) return null

    return (
        <PeopleListCard
            data={list}
            isLoading={isLoading}
            label={label}
            pagination={pagination}
            handleExpand={togglePageSize}
            expanded={expanded}
            searchKey={searchKey}
            handleSearch={setSearchKey}
            showMore={totalCount > initialPageSize}
        />
    )
}

// The wrapper component that provides the context
export const ResourceApproversList: FC<ApproversProps> = ({
    resourceId,
    contextKey,
    ...restProps
}) => {
    // Create a stable context key
    const contextValue =
        contextKey || `ResourceApprovers-${resourceId || 'unknown'}`

    return (
        <PageContextProvider key={contextValue}>
            <ResourceApproversListInner
                resourceId={resourceId}
                {...restProps}
            />
        </PageContextProvider>
    )
}
